import { FieldLabel } from '@measured/puck';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Select } from '../../../page-builder/components/form-fields/Select';
import CustomTooltip from '../../../components/common/tooltip';
import {
  ClickAwayListener,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CustomizeProps,
  StringinterpolationOptions,
} from '../../interface/commonInterface';
import { Switch } from '@mui/material';
import ColorPicker from './ColorPicker';
import { options } from '../../utils/constants';
import InputField from '../../../components/common/input-field';
import { useSearchParams } from 'react-router-dom';
import { fontSizeOptions } from './font';
import { QuillEditor } from '../../../components/common/quill-editor/quill-editor';
import { fontFamilies } from './options';
import { placeHolderHeightOptions } from './height';

export interface FontProps {
  font: string;
}
interface TextCustomInputProps {
  value: CustomizeProps;
  isAccordion?: boolean;
  onChange: (_value: CustomizeProps) => void;
  title?: string;
  showHover?: boolean;
  showTextAlign?: boolean;
  showComponent?: boolean;
  showComponentText?: string;
  timerGap?: string;
  isText?: boolean;
  isLink?: boolean;
  showDeviceType?: boolean;
  quillEditorId?: number; // Optional prop for Quill editor only for component not included in the default props
  isDisableEditor?: boolean;
  isFontFamily?: boolean;
  isLineHeight?: boolean;
  isLetterSpace?: boolean;
}

export const fontWeightOptions = [
  { label: '300', value: 'font-light' },
  { label: '400', value: 'font-normal' },
  { label: '500', value: 'font-medium' },
  { label: '600', value: 'font-semibold' },
  { label: '700', value: 'font-bold' },
  { label: '800', value: 'font-extrabold' },
  { label: '900', value: 'font-black' },
];

export const placeholderfontWeightOptions = [
  { label: '300', value: '300' },
  { label: '400', value: '400' },
  { label: '500', value: '500' },
  { label: '600', value: '600' },
  { label: '700', value: '700' },
  { label: '800', value: '800' },
  { label: '900', value: '900' },
];

const textAlignmentOptions = [
  { label: 'Left', value: 'text-left' },
  { label: 'Center', value: 'text-center' },
  { label: 'Right', value: 'text-right' },
  { label: 'Justify', value: 'text-justify' },
];

const TextCustomInput: React.FC<TextCustomInputProps> = ({
  value,
  onChange,
  isText = true,
  isAccordion = true,
  isLink = false,
  showHover = false,
  showTextAlign = false,
  title,
  showComponent = false,
  showComponentText = 'Show text',
  showDeviceType = true,
  quillEditorId = 1,
  isDisableEditor = false, // Optional prop to disable the editor
  isFontFamily = false, // Optional prop to show font family input
  isLineHeight = false, // Optional prop to show line height input
  isLetterSpace = false, // Optional prop to show letter space input
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
  const [searchParam] = useSearchParams();

  const inputRef = useRef<HTMLInputElement | null>(null);

  const updatedOptions = useCallback((): StringinterpolationOptions[] => {
    return options?.filter(option => {
      if (
        !option.showInTemplates ||
        option.showInTemplates?.length === 0 ||
        option.showInTemplates?.includes(searchParam.get('template') || '')
      ) {
        return true;
      }
      return false;
    });
  }, [searchParam]);

  const handleSelectOption = (key: string) => {
    const cursorPosition = inputRef.current?.selectionStart || 0;
    const newValue =
      value?.text?.substring(0, cursorPosition - 1) +
      `{${key}}` +
      value?.text?.substring(cursorPosition);
    onChange({ ...value, text: newValue });
    setShowDropdown(false);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange({ ...value, text: newValue });

    const cursorPosition = e.target.selectionStart || 0;
    if (newValue[cursorPosition - 1] === '{') {
      setShowDropdown(true);
      setHighlightedIndex(0); // Reset highlight index
      if (inputRef.current) {
        inputRef.current.getBoundingClientRect();
      }
    } else {
      setShowDropdown(false);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const newOptions = updatedOptions();
    if (showDropdown) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex(prev => (prev + 1) % newOptions?.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex(
          prev => (prev - 1 + newOptions?.length) % newOptions?.length,
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const selectedOption = newOptions?.[highlightedIndex];
        handleSelectOption(selectedOption.key);
      } else if (e.key === 'Escape') {
        setShowDropdown(false);
      }
    }
  };

  useEffect(() => {
    if (showDropdown) {
      inputRef.current?.focus();
    }
  }, [showDropdown]);
  const normalizeText = (text: string) => {
    return text.normalize('NFKC'); // Normalize to NFKC form
  };

  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault(); // Prevent default paste behavior

      // Get clipboard data
      const clipboardData = e.clipboardData;
      if (!clipboardData) {
        return;
      }

      const pastedText = clipboardData.getData('text/plain');

      // Normalize and clean the pasted text
      const cleanedText = normalizeText(pastedText);

      // Update the input value with the cleaned text
      if (inputRef.current) {
        const inputElement = inputRef.current;
        const startPosition = inputElement.selectionStart || 0;
        const endPosition = inputElement.selectionEnd || 0;

        // Set the cleaned content into the input field
        const newValue =
          value.text.substring(0, startPosition) +
          cleanedText +
          value.text.substring(endPosition);

        // Update the value
        onChange({ ...value, text: newValue });

        // Move the cursor to the correct position after paste
        setTimeout(() => {
          inputElement.setSelectionRange(
            startPosition + cleanedText.length,
            startPosition + cleanedText.length,
          );
        }, 0);
      }
    };

    // Add the paste event listener to the input element
    if (inputRef.current) {
      inputRef.current.addEventListener('paste', handlePaste);
    }

    // Clean up the event listener when the component unmounts
    return () => {
      if (inputRef.current) {
        inputRef.current.removeEventListener('paste', handlePaste);
      }
    };
  }, [value?.text]);

  return (
    <div>
      {isAccordion && (
        <div
          className="flex justify-between font-bold cursor-pointer py-2 bg-slate-100 rounded-md pagebuilder-theme"
          onClick={() => setIsOpen(!isOpen)}>
          <h3>{title}</h3>
          {!isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
        </div>
      )}
      {(isOpen || !isAccordion) && (
        <>
          {isText && (
            <div className="flex flex-col gap-4 mt-3 items-center border border-gray-300 rounded p-2 w-full">
              <FieldLabel
                label={
                  (
                    <CustomTooltip title="Text">
                      <span>Text</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div"
                className="w-full">
                {/* <InputField
                  value={value.text}
                  onChange={text => onChange({ ...value, text })}
                /> */}
                <ClickAwayListener
                  onClickAway={() => {
                    setShowDropdown(false);
                  }}>
                  <div>
                    {!isDisableEditor ? (
                      <input
                        type="text"
                        ref={inputRef}
                        className="w-full px-4 py-3 rounded-sm"
                        value={value?.text}
                        onKeyDown={handleKeyDown}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => handleTextChange(event)}
                      />
                    ) : (
                      <QuillEditor
                        key={quillEditorId}
                        value={value?.text}
                        enableLink={isLink}
                        onChange={text => onChange({ ...value, text })}
                        id={quillEditorId}
                        isDisableEditor={isDisableEditor}
                      />
                    )}

                    {showDropdown && (
                      <ul
                        className="pagebuilder-theme pop-over-list"
                        style={{
                          position: 'absolute',
                          background: 'black',
                          color: 'white',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          zIndex: 1000,
                          padding: '0',
                          margin: '4px 0',
                          listStyle: 'none',
                          width: '200px',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                        }}>
                        {updatedOptions().map((option, index) => (
                          <li
                            key={option.key}
                            onClick={() => handleSelectOption(option.key)}
                            style={{
                              padding: '8px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              fontSize: '14px',
                              opacity: highlightedIndex === index ? 0.5 : 1,
                            }}>
                            {option.label}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </ClickAwayListener>
              </FieldLabel>
            </div>
          )}
          <div className="flex flex-col gap-4 mt-3 items-center border border-gray-300 rounded p-2 w-full">
            <div className="space-y-2 p-2 w-full flex flex-col gap-2">
              {showDeviceType && (
                <FieldLabel
                  label={
                    (
                      <CustomTooltip title="device">
                        <span>Device</span>
                      </CustomTooltip>
                    ) as React.ReactNode as string
                  }
                  el="div">
                  <ToggleButtonGroup
                    value={value?.deviceType}
                    exclusive
                    className="w-full"
                    onChange={(_e, deviceType) =>
                      onChange({ ...value, deviceType })
                    }
                    aria-label="device">
                    <ToggleButton
                      size="small"
                      disableRipple
                      value="mobile"
                      aria-label="mobile"
                      className="flex-1">
                      Mobile
                    </ToggleButton>
                    <ToggleButton
                      size="small"
                      value="tablet"
                      disableRipple
                      aria-label="tablet"
                      className="flex-1">
                      Tablet
                    </ToggleButton>
                    <ToggleButton
                      size="small"
                      value="desktop"
                      disableRipple
                      aria-label="desktop"
                      className="flex-1">
                      Desktop
                    </ToggleButton>
                  </ToggleButtonGroup>
                </FieldLabel>
              )}
              {showComponent && (
                <div className="flex justify-between items-center mb-3">
                  <p className="text-sm font-semibold">{showComponentText}</p>
                  <Switch
                    checked={value?.showComponent?.[value.deviceType]}
                    onChange={event =>
                      value.showComponent &&
                      onChange({
                        ...value,
                        showComponent: {
                          ...value.showComponent,
                          [value.deviceType]: event.target.checked,
                        },
                      })
                    }
                  />
                </div>
              )}
              <>
                {title === 'Count Down' && (
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip title="Text">
                          <span>Timer Gap</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div">
                    <InputField
                      value={value?.timerGap}
                      onChange={e => {
                        let newValue = e.replace(/[^0-9%]/g, '');

                        // Ensure that only one '%' symbol remains at the end
                        if (newValue?.includes('%')) {
                          newValue =
                            newValue.replace(/%.*%/g, '%').replace(/%/g, '') +
                            '%';
                        }

                        // If the string is empty or contains only whitespace, set to 'auto'
                        if (!newValue.trim()) {
                          newValue = 'auto';
                        }

                        // If the value includes both digits and '%', clean up any non-digit/% characters
                        if (/\d/.test(newValue) && /%/.test(newValue)) {
                          newValue = newValue.replace(/[^0-9%]/g, '');
                        }

                        // Default to 'auto' if the value is still empty
                        if (!newValue.trim()) {
                          newValue = 'auto';
                        }

                        onChange({ ...value, timerGap: newValue });
                      }}
                    />
                  </FieldLabel>
                )}
              </>
              <FieldLabel
                label={
                  (
                    <CustomTooltip title="fontSize">
                      <span>Font Size</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div">
                <Select
                  className="pagebuilder-theme"
                  value={value?.fontSize?.[value.deviceType]}
                  onChange={event =>
                    onChange({
                      ...value,
                      fontSize: {
                        ...value.fontSize,
                        [value.deviceType]: event.target.value,
                      },
                    })
                  }
                  options={fontSizeOptions}
                />
              </FieldLabel>
              <FieldLabel
                label={
                  (
                    <CustomTooltip title="fontWeight">
                      <span>Font Weight</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div">
                <Select
                  value={value?.weight?.[value.deviceType]}
                  onChange={event =>
                    onChange({
                      ...value,
                      weight: {
                        ...value.weight,
                        [value.deviceType]: event.target.value,
                      },
                    })
                  }
                  options={fontWeightOptions}
                />
              </FieldLabel>
              <ColorPicker
                value={value?.textColor?.[value.deviceType]}
                onChange={color =>
                  onChange({
                    ...value,
                    textColor: {
                      ...value.textColor,
                      [value.deviceType]: color,
                    },
                  })
                }
                tooltip="textColor"
                label="Text Color"
              />
              {showHover && (
                <ColorPicker
                  value={value?.hoverColor?.[value.deviceType]}
                  onChange={color =>
                    value?.hoverColor &&
                    onChange({
                      ...value,
                      hoverColor: {
                        ...value.hoverColor,
                        [value.deviceType]: color,
                      },
                    })
                  }
                  tooltip="hoverColor"
                  label="Hover Color"
                />
              )}
              {showTextAlign && (
                <FieldLabel
                  label={
                    (
                      <CustomTooltip title="textAlign">
                        <span>Text Align</span>
                      </CustomTooltip>
                    ) as React.ReactNode as string
                  }
                  el="div">
                  <Select
                    value={value?.textAlign?.[value.deviceType] || ''}
                    onChange={event =>
                      onChange({
                        ...value,
                        textAlign: {
                          mobile: value?.textAlign?.mobile || '',
                          tablet: value?.textAlign?.tablet || '',
                          desktop: value?.textAlign?.desktop || '',
                          [value.deviceType]: event.target.value,
                        },
                      })
                    }
                    options={textAlignmentOptions}
                  />
                </FieldLabel>
              )}
              {isLink && (
                <FieldLabel
                  label={
                    (
                      <CustomTooltip title="fontWeight">
                        <span>Link</span>
                      </CustomTooltip>
                    ) as React.ReactNode as string
                  }
                  el="div">
                  <InputField
                    value={value.linkValue}
                    placeholder="Enter the Link"
                    onChange={val => {
                      onChange({
                        ...value,
                        linkValue: val,
                      });
                    }}
                  />
                </FieldLabel>
              )}
              {isFontFamily && (
                <FieldLabel
                  label={
                    (
                      <CustomTooltip title="fontWeight">
                        <span>Font Family</span>
                      </CustomTooltip>
                    ) as React.ReactNode as string
                  }
                  el="div">
                  <Select
                    value={value?.fontFamily || ''}
                    onChange={event =>
                      onChange({
                        ...value,
                        fontFamily: event.target.value,
                      })
                    }
                    options={[
                      { label: 'Inherit From Global', value: 'inherit-global' },
                      ...fontFamilies,
                    ]}
                  />
                </FieldLabel>
              )}
              {isLineHeight && (
                <FieldLabel
                  label={
                    (
                      <CustomTooltip title="fontWeight">
                        <span>Line Height</span>
                      </CustomTooltip>
                    ) as React.ReactNode as string
                  }
                  el="div">
                  <Select
                    value={value?.lineHeight}
                    onChange={event =>
                      onChange({
                        ...value,
                        lineHeight: event.target.value,
                      })
                    }
                    options={placeHolderHeightOptions}
                  />
                </FieldLabel>
              )}
              {isLetterSpace && (
                <FieldLabel
                  label={
                    (
                      <CustomTooltip title="Text">
                        <span>Letter Space</span>
                      </CustomTooltip>
                    ) as React.ReactNode as string
                  }
                  el="div">
                  <InputField
                    value={value?.letterSpace}
                    onChange={e => {
                      const text = e.replace(/[^0-9%]/g, '');
                      onChange({ ...value, letterSpace: text });
                    }}
                  />
                </FieldLabel>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TextCustomInput;
